import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { useMemberships, useSwitchOrg } from '@/hooks/use-memberships';
import { isOfflineDiscardCancelled } from '@/lib/offline/session-offline-data';

export function SelectOrganizationPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { data: memberships = [], isLoading, isError } = useMemberships();
  const switchOrg = useSwitchOrg();
  const [error, setError] = useState<string | null>(null);

  const handleSelect = async (organizationId: number) => {
    setError(null);
    try {
      await switchOrg.mutateAsync(organizationId);
      await refreshUser();
      navigate('/', { replace: true });
    } catch (error) {
      if (isOfflineDiscardCancelled(error)) return;
      setError('Unable to switch organizations. Please try again.');
    }
  };

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-xl flex-col gap-6 px-6 py-12">
      <header>
        <h1 className="text-2xl font-semibold">Choose an organization</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Select an organization where you still have access.
        </p>
      </header>

      {isLoading && <p role="status">Loading organizations…</p>}
      {isError && <p role="alert">Unable to load your organizations.</p>}
      {error && <p role="alert">{error}</p>}

      <ul className="flex flex-col gap-3">
        {memberships.map((membership) => (
          <li key={membership.organization_id}>
            <button
              type="button"
              className="w-full rounded-md border px-4 py-3 text-left hover:bg-accent disabled:opacity-50"
              disabled={switchOrg.isPending}
              onClick={() => void handleSelect(membership.organization_id)}
            >
              <span className="block font-medium">{membership.organization_name}</span>
              <span className="block text-sm text-muted-foreground">{membership.role}</span>
            </button>
          </li>
        ))}
      </ul>
    </main>
  );
}
